<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Client Name Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('financialReports.clientNamePlaceholder')"
                v-model.trim="filterOptions.client_name"
              />
              <!-- End:: Client Name Input -->

              <!-- Start:: Client Mobile Input -->
              <!-- <base-input
                col="3"
                type="text"
                :placeholder="$t('financialReports.clientMobilePlaceholder')"
                v-model.trim="filterOptions.client_mobile"
              /> -->
              <!-- End:: Client Mobile Input -->
              <!-- Start:: Provider Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('financialReports.providerPlaceholder')"
                v-model.trim="filterOptions.vendor"
              />
              <!-- End:: Provider Input -->

              <!-- Start:: Operation Type Select -->
              <!-- <base-select-input
                col="3"
                :optionsList="operationTypeOptions"
                :placeholder="$t('financialReports.operationType')"
                v-model="filterOptions.type"
              /> -->
              <!-- End:: Operation Type Select -->

              <!-- Start:: From Date Input -->
              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('financialReports.fromDate')"
                v-model.trim="filterOptions.from_date"
              />
              <!-- End:: From Date Input -->

              <!-- Start:: To Date Input -->
              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('financialReports.toDate')"
                v-model.trim="filterOptions.to_date"
              />
              <!-- End:: To Date Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("financialReports.bookingReports") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper">
          <base-button
            class="mt-0 excel_btn"
            styleType="solid_btn"
            :btnText="$t('financialReports.exportExcel')"
            @fireClick="exportToExcel"
            :disabled="isExporting"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-excel"></i>
            </template>
          </base-button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('financialReports.loadingReports')"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("financialReports.noReports") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.serialNumber`]="{ item }">
          <p>{{ item.serialNumber }}</p>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: Booking Number -->
        <template v-slot:[`item.booking_number`]="{ item }">
          <p class="font-weight-bold">{{ item.booking_number }}</p>
        </template>
        <!-- End:: Booking Number -->

        <!-- Start:: Provider Name -->
        <template v-slot:[`item.provider.name`]="{ item }">
          <p>{{ item.provider?.name || "-" }}</p>
        </template>
        <!-- End:: Provider Name -->

        <!-- Start:: Client Name -->
        <template v-slot:[`item.client.name`]="{ item }">
          <p>{{ item.client?.name || "-" }}</p>
        </template>
        <!-- End:: Client Name -->

        <!-- Start:: Booking Date -->
        <template v-slot:[`item.created_at`]="{ item }">
          <p>{{ item.created_at }}</p>
        </template>
        <!-- End:: Booking Date -->

        <!-- Start:: Booking Duration -->
        <template v-slot:[`item.total_days`]="{ item }">
          <p>{{ item.total_days }} {{ $t("financialReports.days") }}</p>
        </template>
        <!-- End:: Booking Duration -->

        <!-- Start:: Total Booking Amount -->
        <template v-slot:[`item.final_total`]="{ item }">
          <p class="font-weight-bold">{{ formatNumber(item.final_total) }}</p>
        </template>
        <!-- End:: Total Booking Amount -->

        <!-- Start:: Provider Profit -->
        <template v-slot:[`item.provider_profit`]="{ item }">
          <p class="text-success">{{ formatNumber(item.provider_profit) }}</p>
        </template>
        <!-- End:: Provider Profit -->

        <!-- Start:: Admin Profit -->
        <template v-slot:[`item.management_profit`]="{ item }">
          <p class="text-primary">{{ formatNumber(item.management_profit) }}</p>
        </template>
        <!-- End:: Admin Profit -->

        <!-- Start:: Status -->
        <template v-slot:[`item.status`]="{ item }">
          <span
            class="status_badge"
            :class="{
              'text-success': item.status === 'finished',
              'text-warning': item.status === 'pending',
              'text-danger': item.status === 'cancelled',
            }"
          >
            {{ item.status_trans }}
          </span>
        </template>
        <!-- End:: Status -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->

      <!--  =========== Start:: Financial Summary Section =========== -->
      <div class="table_content mt-5">
        <h5 class="summary_title mb-3">
          {{ $t("financialReports.summarySection") }}
        </h5>
        <v-simple-table class="stat-table">
          <template v-slot:default>
            <thead class="all-stat">
              <tr>
                <th class="text-center" style="color: white !important">
                  {{ $t("financialReports.totalBookingAmountSummary") }}
                </th>
                <th class="text-center" style="color: white !important">
                  {{ $t("financialReports.totalAdminProfit") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr class="text-center">
                <td class="font-weight-bold">
                  {{ formatNumber(totals.total_booking_amount) }}
                </td>
                <td class="font-weight-bold">
                  {{ formatNumber(totals.management_profit) }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
      <!--  =========== End:: Financial Summary Section =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        ></v-pagination>
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "FinancialReports",

  data() {
    return {
      // Start:: Loader Control Data
      loading: false,
      isWaitingRequest: false,
      isExporting: false,
      // End:: Loader Control Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        from_date: null,
        to_date: null,
        client_name: null,
        client_mobile: null,
        vendor: null,
        type: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      tableHeaders: [
        {
          text: this.$t("financialReports.serialNumber"),
          value: "serialNumber",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("financialReports.bookingNumber"),
          value: "booking_number",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("financialReports.providerName"),
          value: "provider.name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("financialReports.clientName"),
          value: "client.name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("financialReports.bookingDate"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("financialReports.bookingDuration"),
          value: "total_days",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("financialReports.totalBookingAmount"),
          value: "final_total",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("financialReports.providerProfitAfterDeduction"),
          value: "provider_profit",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("financialReports.adminProfitAmount"),
          value: "management_profit",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("financialReports.bookingStatus"),
          value: "status",
          align: "center",
          sortable: false,
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data

      // Start:: Operation Type Options
      operationTypeOptions: [
        {
          id: null,
          name: this.$t("financialReports.allTypes"),
        },
        {
          id: "loyalty_points",
          name: this.$t("financialReports.loyaltyPoints"),
        },
        {
          id: "gift_card",
          name: this.$t("financialReports.giftCard"),
        },
        {
          id: "discount_card",
          name: this.$t("financialReports.discountCard"),
        },
      ],
      // End:: Operation Type Options

      // Start:: Totals Data
      totals: {
        total_booking_amount: 0,
        management_profit: 0,
      },
      // End:: Totals Data

      // Start:: Excel Export URL
      excelReportUrl: null,
      // End:: Excel Export URL
    };
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
  },

  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.getAllReports();
    },
  },

  methods: {
    // Start:: Format Number to 2 Decimal Places
    formatNumber(value) {
      if (!value && value !== 0) return "0.00";
      return parseFloat(value).toFixed(2);
    },
    // End:: Format Number to 2 Decimal Places

    // Start:: Get All Reports
    async getAllReports() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "reports/financial-reports",
          params: {
            page: this.paginations.current_page,
            from_date: this.filterOptions.from_date,
            to_date: this.filterOptions.to_date,
            client_name: this.filterOptions.client_name,
            client_mobile: this.filterOptions.client_mobile,
            vendor: this.filterOptions.vendor,
            type: this.filterOptions.type?.id,
          },
        });
        this.loading = false;

        // Set table rows with serial numbers
        this.tableRows = res.data.data.bookings || [];
        this.tableRows.forEach((item, index) => {
          item.serialNumber =
            (this.paginations.current_page - 1) *
              this.paginations.items_per_page +
            index +
            1;
        });

        // Set totals
        this.totals.total_booking_amount =
          res.data.data.total_budget?.total_booking_amount || 0;
        this.totals.management_profit =
          res.data.data.total_budget?.management_profit || 0;

        // Set excel export URL
        this.excelReportUrl = res.data.data.excel_report || null;

        // Set pagination
        if (res.data.meta) {
          this.paginations.last_page = res.data.meta.last_page;
          this.paginations.items_per_page = res.data.meta.per_page;
        }
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
      }
    },
    // End:: Get All Reports

    // Start:: Submit Filter Form
    submitFilterForm() {
      this.paginations.current_page = 1;
      this.getAllReports();
    },
    // End:: Submit Filter Form

    // Start:: Reset Filter
    resetFilter() {
      this.filterOptions.from_date = null;
      this.filterOptions.to_date = null;
      this.filterOptions.client_name = null;
      this.filterOptions.client_mobile = null;
      this.filterOptions.vendor = null;
      this.filterOptions.type = null;
      this.paginations.current_page = 1;
      this.getAllReports();
    },
    // End:: Reset Filter

    // Start:: Export to Excel
    async exportToExcel() {
      if (!this.excelReportUrl) {
        this.$message.error("Export URL not available");
        return;
      }

      this.isExporting = true;
      try {
        // Build query params based on active filters
        const params = new URLSearchParams();
        if (this.filterOptions.from_date)
          params.append("from_date", this.filterOptions.from_date);
        if (this.filterOptions.to_date)
          params.append("to_date", this.filterOptions.to_date);
        if (this.filterOptions.client_name)
          params.append("client_name", this.filterOptions.client_name);
        if (this.filterOptions.client_mobile)
          params.append("client_mobile", this.filterOptions.client_mobile);
        if (this.filterOptions.vendor)
          params.append("vendor", this.filterOptions.vendor);
        if (this.filterOptions.type)
          params.append("type", this.filterOptions.type);

        const urlWithParams = params.toString()
          ? `${this.excelReportUrl}?${params.toString()}`
          : this.excelReportUrl;

        // Open the URL to trigger download
        window.open(urlWithParams, "_blank");

        this.$message.success(this.$t("financialReports.exportSuccess"));
      } catch (error) {
        console.log(error);
        this.$message.error("Export failed");
      } finally {
        this.isExporting = false;
      }
    },
    // End:: Export to Excel

    // Start:: Update Router Query Param
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
    // End:: Update Router Query Param
  },

  created() {
    this.paginations.current_page = Number(this.$route.query.page) || 1;
    this.getAllReports();
  },
};
</script>

<style scoped lang="scss">
.summary_title {
  font-weight: 700;
  color: var(--main_theme_clr);
  text-align: center;
}

.status_badge {
  padding: 4px 12px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 13px;
}

.all-stat {
  background: var(--main_theme_clr);
}

.all-stat th {
  color: white !important;
  font-size: 16px !important;
  font-weight: 800;
}

.stat-table {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;

  tbody td {
    font-size: 15px;
    padding: 16px !important;
  }
}
</style>
